import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api, formatBRL, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Download, LogOut, Trash2, Plus, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import ProductCard from "@/components/ProductCard";

export default function Account() {
  const { user, loading, logout, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "pedidos";
  const setTab = (t) => { const n = new URLSearchParams(params); n.set("tab", t); setParams(n); };

  if (loading) return <div className="p-20 text-center text-[#49392F]/70">Carregando...</div>;
  if (!user) return <Navigate to="/login" />;

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12" data-testid="account-page">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-[#C2A064]">Minha área</p>
          <h1 className="font-display text-4xl text-[#49392F] mt-2">Olá, {user.name.split(" ")[0]}</h1>
        </div>
        <button onClick={logout} className="text-[12px] tracking-[0.3em] uppercase text-[#49392F] inline-flex items-center gap-2 link-elegant" data-testid="logout-button">
          <LogOut size={14} /> Sair
        </button>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-[#C2A064]/20 mb-10">
        {[
          { k: "pedidos", label: "Pedidos" },
          { k: "favoritos", label: "Favoritos" },
          { k: "enderecos", label: "Endereços" },
          { k: "dados", label: "Meus dados" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-5 py-3 text-[11px] tracking-[0.25em] uppercase border-b-2 -mb-px ${tab === t.k ? "border-[#49392F] text-[#49392F]" : "border-transparent text-[#49392F]/60 hover:text-[#49392F]"}`}
            data-testid={`tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "pedidos" && <Orders />}
      {tab === "favoritos" && <Favorites />}
      {tab === "enderecos" && <Addresses />}
      {tab === "dados" && <ProfileData user={user} onUpdated={refresh} />}
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search] = useSearchParams();
  const highlight = search.get("destaque");
  const [pixMap, setPixMap] = useState({});
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    api.get("/me/orders").then(({ data }) => setOrders(data || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // Fetch Pix for the highlighted (just-created) order
    if (!highlight) return;
    api.get(`/me/orders/${highlight}/pix`).then(({ data }) => setPixMap((m) => ({ ...m, [highlight]: data }))).catch(() => {});
  }, [highlight]);

  const copyPix = async (code) => {
    try { await navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(null), 2000); } catch { /* noop */ }
  };

  if (loading) return <div className="text-[#49392F]/70">Carregando...</div>;
  if (orders.length === 0) return <p className="text-[#49392F]/70">Você ainda não fez pedidos.</p>;

  const token = localStorage.getItem("duo_token");
  const download = async (o) => {
    const r = await fetch(`${API}/me/orders/${o.id}/receipt`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `recibo-${o.order_number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="orders-list">
      {orders.map((o) => {
        const isHighlight = o.id === highlight;
        const pix = pixMap[o.id];
        return (
          <div key={o.id} className={`bg-white border p-6 ${isHighlight ? "border-[#C2A064] shadow-[0_6px_30px_rgba(194,160,100,0.2)]" : "border-[#C2A064]/20"}`} data-testid={`order-${o.order_number}`}>
            {isHighlight && (
              <div className="mb-5 pb-5 border-b border-[#C2A064]/20">
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#C2A064]">Pedido recebido!</div>
                <div className="font-display text-2xl text-[#49392F] mt-1">Obrigada pela sua compra ✨</div>
                <p className="text-[13px] text-[#49392F]/80 mt-2">Seu recibo em PDF está disponível abaixo e no e-mail cadastrado.</p>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#C2A064]">Pedido</div>
                <div className="font-display text-xl text-[#49392F] mt-1">{o.order_number}</div>
                <div className="text-[12px] text-[#49392F]/60 mt-1">{new Date(o.created_at).toLocaleString("pt-BR")}</div>
              </div>
              <div className="text-right">
                <span className="inline-block px-3 py-1 text-[11px] tracking-[0.2em] uppercase bg-[#E8DCCB] text-[#49392F]">{o.status_label}</span>
                <div className="font-display text-xl text-[#49392F] mt-2">{formatBRL(o.total)}</div>
              </div>
            </div>
            <div className="hairline my-5" />
            <div className="space-y-2">
              {o.items.map((i, idx) => (
                <div key={idx} className="flex justify-between text-[13px] text-[#49392F]">
                  <span>{i.quantity}x {i.name} {i.size ? `• ${i.size}` : ""}{i.pair ? ` • ${i.pair.name_1}/${i.pair.name_2}` : ""}</span>
                  <span>{formatBRL(i.subtotal)}</span>
                </div>
              ))}
              {o.discount_amount ? (
                <div className="flex justify-between text-[12px] text-[#C2A064]">
                  <span>Cupom {o.coupon_code} ({Math.round(o.discount_pct || 0)}%)</span><span>- {formatBRL(o.discount_amount)}</span>
                </div>
              ) : null}
            </div>
            {o.tracking_code && (
              <div className="mt-4 text-[12px] text-[#49392F]/80">Código de rastreamento: <strong>{o.tracking_code}</strong></div>
            )}
            {isHighlight && pix?.payload && (
              <div className="mt-5 pt-5 border-t border-[#C2A064]/20">
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#C2A064] mb-2">Pague com Pix agora</div>
                <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-4 items-center">
                  <img src={`${API}/me/orders/${o.id}/pix.png`} alt="QR Pix" className="w-full h-auto border border-[#E8DCCB]" style={{ imageRendering: "pixelated" }} data-testid="pix-qr-mini" />
                  <div>
                    <div className="text-[12px] text-[#49392F]/80 mb-2">Valor: <b>{formatBRL(pix.amount)}</b> para <b>{pix.merchant_name}</b></div>
                    <button type="button" onClick={() => copyPix(pix.payload)} className="duo-btn duo-btn-primary" data-testid={`copy-pix-${o.order_number}`}>
                      {copied === pix.payload ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar código Pix</>}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="mt-5 flex gap-3">
              <button onClick={() => download(o)} className="duo-btn duo-btn-secondary" data-testid={`download-${o.order_number}`}>
                <Download size={14} /> Baixar recibo PDF
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Favorites() {
  const [favs, setFavs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/favorites").then(({ data }) => setFavs(data || [])).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-[#49392F]/70">Carregando...</div>;
  if (favs.length === 0) return <p className="text-[#49392F]/70">Nenhum favorito ainda.</p>;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10" data-testid="favorites-list">
      {favs.map((p) => <ProductCard key={p.id} product={p} />)}
    </div>
  );
}

function Addresses() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ label: "Endereço", zip: "", street: "", number: "", complement: "", district: "", city: "", state: "" });
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/me/addresses").then(({ data }) => setList(data || []));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    await api.post("/me/addresses", form);
    toast.success("Endereço salvo");
    setForm({ label: "Endereço", zip: "", street: "", number: "", complement: "", district: "", city: "", state: "" });
    setAdding(false);
    load();
  };
  const del = async (id) => { await api.delete(`/me/addresses/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="addresses-list">
      {list.map((a) => (
        <div key={a.id} className="bg-white border border-[#C2A064]/20 p-5 flex justify-between items-start">
          <div>
            <div className="font-display text-lg text-[#49392F]">{a.label}</div>
            <div className="text-[13px] text-[#49392F]/80 mt-1">{a.street}, {a.number} {a.complement}</div>
            <div className="text-[13px] text-[#49392F]/70">{a.district} — {a.city}/{a.state} • CEP {a.zip}</div>
          </div>
          <button onClick={() => del(a.id)} className="text-[#49392F]/60 hover:text-[#49392F]" aria-label="remover"><Trash2 size={16} /></button>
        </div>
      ))}
      {!adding ? (
        <button onClick={() => setAdding(true)} className="duo-btn duo-btn-secondary inline-flex"><Plus size={14} /> Adicionar endereço</button>
      ) : (
        <form onSubmit={save} className="bg-white border border-[#C2A064]/20 p-6 grid grid-cols-2 gap-3">
          <input className="duo-input col-span-2 sm:col-span-1" placeholder="Rótulo (Casa, trabalho...)" value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} />
          <input className="duo-input col-span-2 sm:col-span-1" placeholder="CEP" value={form.zip} onChange={(e) => setForm({...form, zip: e.target.value})} />
          <input className="duo-input col-span-2" placeholder="Rua" value={form.street} onChange={(e) => setForm({...form, street: e.target.value})} />
          <input className="duo-input col-span-2 sm:col-span-1" placeholder="Número" value={form.number} onChange={(e) => setForm({...form, number: e.target.value})} />
          <input className="duo-input col-span-2 sm:col-span-1" placeholder="Complemento" value={form.complement} onChange={(e) => setForm({...form, complement: e.target.value})} />
          <input className="duo-input col-span-2 sm:col-span-1" placeholder="Bairro" value={form.district} onChange={(e) => setForm({...form, district: e.target.value})} />
          <input className="duo-input col-span-2 sm:col-span-1" placeholder="Cidade" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} />
          <input className="duo-input col-span-2 sm:col-span-1" placeholder="UF" value={form.state} onChange={(e) => setForm({...form, state: e.target.value.toUpperCase().slice(0,2)})} />
          <div className="col-span-2 flex gap-3">
            <button className="duo-btn duo-btn-primary" type="submit">Salvar</button>
            <button type="button" className="duo-btn duo-btn-secondary" onClick={() => setAdding(false)}>Cancelar</button>
          </div>
        </form>
      )}
    </div>
  );
}

function ProfileData({ user, onUpdated }) {
  const [form, setForm] = useState({ name: user.name, phone: user.phone || "", cpf: user.cpf || "" });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/me", form);
      await onUpdated();
      toast.success("Dados atualizados");
    } finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="max-w-lg space-y-3" data-testid="profile-form">
      <div className="text-[12px] text-[#49392F]/70 mb-2">E-mail: <strong>{user.email}</strong></div>
      <input className="duo-input" placeholder="Nome" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
      <input className="duo-input" placeholder="WhatsApp" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
      <input className="duo-input" placeholder="CPF (opcional, para futura NF-e)" value={form.cpf} onChange={(e) => setForm({...form, cpf: e.target.value})} />
      <button disabled={saving} className="duo-btn duo-btn-primary">Salvar alterações</button>
    </form>
  );
}
